import { useEffect, useState } from 'react';
import '@/App.css';
import { 
  ShieldCheck, 
  Lock, 
  UserCheck, 
  Stethoscope, 
  Clock, 
  MapPin, 
  Phone, 
  MessageCircle,
  Award,
  CheckCircle2,
  AlertCircle,
  ChevronRight
} from 'lucide-react';

const PHONE = '9559758479';
const WHATSAPP = '9760336942';
const ADDRESS = 'CM 24, Sector 122, Noida';
const MAP_LINK = 'https://maps.google.com/?q=CM+24+Sector+122+Noida';

function App() {
  const [showStickyBar, setShowStickyBar] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowStickyBar(true);
      } else {
        setShowStickyBar(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCall = () => {
    window.location.href = `tel:${PHONE}`;
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${WHATSAPP}?text=Hi,%20I%20would%20like%20to%20book%20a%20confidential%20consultation`, '_blank');
  };

  return (
    <div className="App">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center bg-gradient-to-b from-white to-soft-blue overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-5">
          <div className="absolute inset-0" style={{
            background: 'radial-gradient(circle at 50% 50%, rgba(3, 105, 161, 0.1) 0%, transparent 70%)'
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-24 grid md:grid-cols-2 gap-12 items-center relative z-10">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-soft-blue rounded-full border border-trust-blue/20">
              <ShieldCheck className="w-4 h-4 text-trust-blue" />
              <span className="text-sm font-accent font-medium text-trust-blue">100% Confidential Treatment</span>
            </div>
            
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-text-primary tracking-tight leading-tight">
              Private. Professional. <span className="text-trust-blue">Trusted.</span>
            </h1>
            
            <p className="text-lg md:text-xl text-text-secondary font-body leading-relaxed">
              Leading STD & Sexual Health Clinic in Noida by Dr. Ankur Lal (AIIMS Trained)
            </p>
            
            {/* Trust Badges */}
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 text-text-secondary">
                <CheckCircle2 className="w-5 h-5 text-safety-green" />
                <span className="text-sm font-body">Same Day Results</span>
              </div>
              <div className="flex items-center gap-2 text-text-secondary">
                <CheckCircle2 className="w-5 h-5 text-safety-green" />
                <span className="text-sm font-body">Expert Care</span>
              </div>
              <div className="flex items-center gap-2 text-text-secondary">
                <CheckCircle2 className="w-5 h-5 text-safety-green" />
                <span className="text-sm font-body">No Judgment</span>
              </div>
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button 
                data-testid="hero-call-button"
                onClick={handleCall}
                className="flex items-center justify-center gap-3 px-8 py-4 bg-trust-blue text-white rounded-full font-accent font-medium text-lg shadow-lg hover:shadow-xl hover:bg-trust-blue/90 transition-all duration-300 transform hover:scale-105"
              >
                <Phone className="w-5 h-5" />
                Call Now
              </button>
              <button 
                data-testid="hero-whatsapp-button"
                onClick={handleWhatsApp}
                className="flex items-center justify-center gap-3 px-8 py-4 bg-safety-green text-white rounded-full font-accent font-medium text-lg shadow-lg hover:shadow-xl hover:bg-safety-green/90 transition-all duration-300 transform hover:scale-105"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp
              </button>
            </div>
          </div>
          
          {/* Right Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_confidential-care-3/artifacts/vnn1xi80_8C35964F-D9DD-4805-BAE7-D35EC04BD344.jpeg" 
                alt="Dr Ankur Lal - Professional Dermatologist" 
                className="w-full h-auto"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6">
                <p className="text-white font-heading font-semibold text-xl">Dr. Ankur Lal</p>
                <p className="text-white/90 font-body text-sm">MBBS, MD Dermatology (AIIMS)</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section data-testid="why-choose-us-section" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">
              Why Choose Our Clinic?
            </h2>
            <p className="text-lg text-text-secondary font-body max-w-2xl mx-auto">
              We provide judgment-free, confidential care with the latest medical treatments
            </p>
          </div>
          
          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Feature 1 - Larger */}
            <div className="md:col-span-2 bg-soft-blue p-8 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border border-trust-blue/10">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0 w-16 h-16 bg-trust-blue rounded-2xl flex items-center justify-center">
                  <Lock className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-2xl text-text-primary mb-3">
                    100% Confidential Treatment
                  </h3>
                  <p className="text-text-secondary font-body leading-relaxed">
                    Your privacy is our top priority. We ensure complete confidentiality with private consultation rooms and secure medical records. No personal data sharing.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Feature 2 */}
            <div className="bg-gradient-to-br from-safety-green/5 to-safety-green/10 p-8 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border border-safety-green/20">
              <div className="w-14 h-14 bg-safety-green rounded-2xl flex items-center justify-center mb-4">
                <Award className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
                AIIMS-Trained Specialist
              </h3>
              <p className="text-text-secondary font-body text-sm leading-relaxed">
                Dr. Ankur Lal is a Gold Medalist in MD Dermatology from AIIMS with over 10 years of experience
              </p>
            </div>
            
            {/* Feature 3 */}
            <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200">
              <div className="w-14 h-14 bg-trust-blue rounded-2xl flex items-center justify-center mb-4">
                <Clock className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
                Same-Day Diagnosis
              </h3>
              <p className="text-text-secondary font-body text-sm leading-relaxed">
                Fast and accurate diagnosis with advanced testing facilities available on-site
              </p>
            </div>
            
            {/* Feature 4 */}
            <div className="md:col-span-2 bg-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0 w-16 h-16 bg-safety-green rounded-2xl flex items-center justify-center">
                  <Stethoscope className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-2xl text-text-primary mb-3">
                    Advanced Painless Procedures
                  </h3>
                  <p className="text-text-secondary font-body leading-relaxed">
                    We use the latest medical technology for painless treatment procedures ensuring your comfort throughout the healing process.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conditions We Treat */}
      <section data-testid="conditions-section" className="py-16 md:py-24 bg-background-alt">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">
              Conditions We Treat
            </h2>
            <p className="text-lg text-text-secondary font-body max-w-2xl mx-auto">
              Specialized treatment for all STDs and sexual health concerns
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: 'Genital Warts (HPV)', icon: AlertCircle, symptoms: 'Small bumps or growths in genital area' },
              { name: 'Genital Herpes', icon: AlertCircle, symptoms: 'Painful blisters or sores in genital region' },
              { name: 'Syphilis', icon: AlertCircle, symptoms: 'Painless sores, rashes, or flu-like symptoms' },
              { name: 'Gonorrhea', icon: AlertCircle, symptoms: 'Painful urination, discharge, or pelvic pain' },
              { name: 'Genital Ulcer', icon: AlertCircle, symptoms: 'Open sores or ulcers in the genital area' },
              { name: 'Genital Discharge', icon: AlertCircle, symptoms: 'Unusual discharge with odor or discomfort' },
              { name: 'Genital Rashes', icon: AlertCircle, symptoms: 'Skin rashes, itching, or irritation in genital area' },
              { name: 'Premature Ejaculation', icon: UserCheck, symptoms: 'Early ejaculation affecting intimate life' },
              { name: 'Molluscum Contagiosum', icon: AlertCircle, symptoms: 'Small, firm, dome-shaped bumps on skin' },
              { name: 'HIV Counseling & Testing', icon: UserCheck, symptoms: 'Confidential testing and support services' },
            ].map((condition, index) => (
              <div 
                key={index}
                data-testid={`condition-card-${index}`}
                className="bg-white p-6 rounded-2xl shadow-md hover:shadow-xl hover:border-trust-blue transition-all duration-300 border-2 border-transparent cursor-pointer group"
              >
                <condition.icon className="w-10 h-10 text-trust-blue mb-4 group-hover:scale-110 transition-transform duration-300" />
                <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                  {condition.name}
                </h3>
                <p className="text-sm text-text-secondary font-body">
                  {condition.symptoms}
                </p>
              </div>
            ))}
          </div>
          
          <div className="mt-8 p-6 bg-amber-50 border-l-4 border-amber-500 rounded-lg">
            <p className="text-text-primary font-body font-medium">
              <span className="font-semibold">Don't ignore these signs.</span> Early detection and treatment can prevent serious complications. Book your confidential consultation today.
            </p>
          </div>
        </div>
      </section>

      {/* Doctor Profile */}
      <section data-testid="doctor-profile-section" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-5 gap-12 items-center">
            {/* Doctor Image */}
            <div className="md:col-span-2">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_confidential-care-3/artifacts/vnn1xi80_8C35964F-D9DD-4805-BAE7-D35EC04BD344.jpeg" 
                  alt="Dr Ankur Lal" 
                  className="w-full h-auto"
                />
              </div>
            </div>
            
            {/* Doctor Details */}
            <div className="md:col-span-3 space-y-6">
              <div>
                <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-2">
                  Dr. Ankur Lal
                </h2>
                <p className="text-lg text-trust-blue font-body font-medium">
                  MBBS, MD Dermatology (Gold Medalist)
                </p>
              </div>
              
              <div className="space-y-4">
                {[
                  { label: 'Education', value: 'MBBS, MD Dermatology from AIIMS' },
                  { label: 'Specialization', value: 'STD & Sexual Health, Dermatology' },
                  { label: 'Experience', value: '10+ Years in Dermatology & STD Treatment' },
                  { label: 'Recognition', value: 'Gold Medalist in MD Dermatology' },
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <ChevronRight className="w-5 h-5 text-trust-blue flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-heading font-semibold text-text-primary">{item.label}</p>
                      <p className="text-text-secondary font-body">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="bg-soft-blue p-6 rounded-xl border border-trust-blue/20 mt-6">
                <p className="text-text-primary font-body leading-relaxed italic">
                  "We understand your concern. Your privacy is completely safe with us. I ensure every patient receives personalized, judgment-free care in a comfortable environment."
                </p>
                <p className="text-trust-blue font-heading font-semibold mt-2">- Dr. Ankur Lal</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Confidentiality Promise */}
      <section data-testid="confidentiality-section" className="py-16 md:py-24 bg-gradient-to-br from-trust-blue to-trust-blue/90 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1637095937545-7d8c1edf4d2b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBtZWRpY2FsJTIwY2xpbmljJTIwcmVjZXB0aW9uJTIwaW50ZXJpb3IlMjBjbGVhbiUyMGJyaWdodHxlbnwwfHx8fDE3NzM5MzYxMjN8MA&ixlib=rb-4.1.0&q=85" 
            alt="Consultation Room" 
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mb-6">
              <ShieldCheck className="w-12 h-12 text-white" />
            </div>
            
            <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">
              Your Privacy is Our Priority
            </h2>
            
            <p className="text-xl mb-8 font-body leading-relaxed text-white/90">
              We understand the sensitive nature of sexual health. Your visit will remain completely confidential.
            </p>
            
            <div className="grid sm:grid-cols-3 gap-6 mt-12">
              {[
                { icon: Lock, title: 'Private Consultation Rooms', desc: 'Discreet, soundproof rooms for your comfort' },
                { icon: ShieldCheck, title: 'No Personal Data Sharing', desc: 'Your medical records are completely secure' },
                { icon: UserCheck, title: 'Discreet Treatment Process', desc: 'Professional, judgment-free environment' },
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full mb-4">
                    <item.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg mb-2">{item.title}</h3>
                  <p className="text-sm text-white/80 font-body">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="testimonials-section" className="py-16 md:py-24 bg-background-alt">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">
              What Our Patients Say
            </h2>
            <p className="text-lg text-text-secondary font-body">
              Real experiences from satisfied patients
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                text: "I was very anxious about visiting, but the staff and Dr. Lal made me feel comfortable immediately. The treatment was completely private and professional. Highly recommend!",
                name: "Patient from Sector 18",
                condition: "Genital Warts Treatment"
              },
              {
                text: "Dr. Ankur Lal is very understanding and explains everything clearly. The clinic maintains complete privacy. I felt safe and respected throughout my treatment.",
                name: "Patient from Greater Noida",
                condition: "STD Testing & Treatment"
              },
              {
                text: "The best part was the non-judgmental atmosphere. Dr. Lal's expertise and the confidential environment helped me recover quickly without any stress.",
                name: "Patient from Noida Extension",
                condition: "Herpes Treatment"
              },
            ].map((testimonial, index) => (
              <div 
                key={index}
                data-testid={`testimonial-${index}`}
                className="bg-white p-6 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="mb-4">
                  <div className="flex gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-amber-400 fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-text-secondary font-body leading-relaxed mb-4">
                    "{testimonial.text}"
                  </p>
                </div>
                <div className="border-t pt-4">
                  <p className="font-heading font-semibold text-text-primary">{testimonial.name}</p>
                  <p className="text-sm text-text-muted font-body">{testimonial.condition}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Strong CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-safety-green to-safety-green/90 text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">
            Don't Ignore Symptoms – Early Treatment Saves You
          </h2>
          <p className="text-xl mb-8 font-body text-white/90">
            Get confidential expert care today. Your health matters, and your privacy is guaranteed.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              data-testid="cta-call-button"
              onClick={handleCall}
              className="flex items-center justify-center gap-3 px-8 py-4 bg-white text-safety-green rounded-full font-accent font-medium text-lg shadow-lg hover:shadow-xl hover:bg-gray-50 transition-all duration-300 transform hover:scale-105"
            >
              <Phone className="w-5 h-5" />
              Call {PHONE}
            </button>
            <button 
              data-testid="cta-whatsapp-button"
              onClick={handleWhatsApp}
              className="flex items-center justify-center gap-3 px-8 py-4 bg-white text-safety-green rounded-full font-accent font-medium text-lg shadow-lg hover:shadow-xl hover:bg-gray-50 transition-all duration-300 transform hover:scale-105"
            >
              <MessageCircle className="w-5 h-5" />
              WhatsApp Now
            </button>
          </div>
        </div>
      </section>

      {/* Location & Contact */}
      <section data-testid="location-section" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">
                  Visit Our Clinic
                </h2>
                <p className="text-lg text-text-secondary font-body">
                  Conveniently located in Noida for easy access
                </p>
              </div>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-soft-blue rounded-xl flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-trust-blue" />
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-text-primary mb-1">Address</p>
                    <p className="text-text-secondary font-body">{ADDRESS}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-soft-blue rounded-xl flex items-center justify-center">
                    <Phone className="w-6 h-6 text-trust-blue" />
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-text-primary mb-1">Phone</p>
                    <a href={`tel:${PHONE}`} className="text-trust-blue font-body hover:underline">{PHONE}</a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-soft-blue rounded-xl flex items-center justify-center">
                    <MessageCircle className="w-6 h-6 text-safety-green" />
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-text-primary mb-1">WhatsApp</p>
                    <button onClick={handleWhatsApp} className="text-safety-green font-body hover:underline">
                      Chat on WhatsApp
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="bg-soft-blue p-6 rounded-xl">
                <p className="font-heading font-semibold text-text-primary mb-2">Clinic Hours</p>
                <p className="text-text-secondary font-body">Monday - Saturday: 10:00 AM - 8:00 PM</p>
                <p className="text-text-secondary font-body">Sunday: By Appointment Only</p>
              </div>
            </div>
            
            {/* Google Map */}
            <div className="h-[400px] md:h-full rounded-2xl overflow-hidden shadow-lg">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3503.9366567890847!2d77.37999997549869!3d28.569679575701656!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce5fc00000001%3A0x1234567890abcdef!2sSector%20122%2C%20Noida%2C%20Uttar%20Pradesh!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Clinic Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-text-primary text-white py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="font-body text-sm text-white/70">
            © 2024 STD Clinic in Noida by Dr. Ankur Lal. All rights reserved. | 100% Confidential Treatment
          </p>
        </div>
      </footer>

      {/* Sticky Mobile CTA */}
      {showStickyBar && (
        <div 
          data-testid="sticky-mobile-cta"
          className="md:hidden fixed bottom-0 left-0 right-0 bg-white shadow-2xl border-t-2 border-trust-blue z-50 p-4 animate-in slide-in-from-bottom duration-300"
        >
          <div className="flex gap-3">
            <button 
              onClick={handleCall}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-trust-blue text-white rounded-full font-accent font-medium shadow-lg active:scale-95 transition-transform"
            >
              <Phone className="w-5 h-5" />
              Call Now
            </button>
            <button 
              onClick={handleWhatsApp}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-safety-green text-white rounded-full font-accent font-medium shadow-lg active:scale-95 transition-transform"
            >
              <MessageCircle className="w-5 h-5" />
              WhatsApp
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;